import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { psAPI, controllingAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  Plus, FileText, ChevronLeft, Eye, XCircle, Edit2, Search, Building2,
  CheckCircle, Clock, AlertCircle, DollarSign, Wallet, TrendingUp, Package, Ruler
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Commitments = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [commitments, setCommitments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wbsElements, setWbsElements] = useState([]);
  const [costElements, setCostElements] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    commitment_status: ''
  });
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCommitment, setSelectedCommitment] = useState(null);

  const emptyForm = {
    project_id: '', wbs_element_id: '', commitment_type: 'MANUAL',
    source_document_number: '', source_document_item: '',
    commitment_amount: 0, currency: 'USD',
    fiscal_year: new Date().getFullYear(), period: new Date().getMonth() + 1,
    cost_element_id: '',
    commitment_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    material_id: '', material_description: '',
    quantity: '', unit_of_measure: '', unit_price: '',
    description: ''
  };

  const [formData, setFormData] = useState(emptyForm);

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [filters]);
  useEffect(() => { fetchCostElements(); fetchUoM(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [commitsRes, projectsRes] = await Promise.all([
        psAPI.getCommitments(filters),
        psAPI.getProjects()
      ]);
      setCommitments(commitsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load commitments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostElements = async () => {
    try {
      // Use real Controlling (CO) master data — active cost elements only.
      const response = await controllingAPI.getCostElements({ status: 'Active' });
      setCostElements(Array.isArray(response.data) ? response.data : []);
    } catch { setCostElements([]); }
  };

  const fetchUoM = async () => {
    try {
      const response = await fetch(`${API_URL}/api/units-of-measure`);
      if (response.ok) {
        const data = await response.json();
        setUnitsOfMeasure(data);
      }
    } catch { setUnitsOfMeasure([]); }
  };

  const fetchWBSElements = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await psAPI.getWBSElements({ project_id: projectId });
      setWbsElements(response.data.filter(w => w.is_account_assignment_element !== false));
    } catch (error) {
      console.error('Error fetching WBS elements:', error);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData(prev => ({ ...prev, project_id: projectId, wbs_element_id: '' }));
    fetchWBSElements(projectId);
  };

  // Auto-calculate commitment_amount from quantity * unit_price
  const updateMaterialCalc = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      const qty = parseFloat(field === 'quantity' ? value : prev.quantity) || 0;
      const price = parseFloat(field === 'unit_price' ? value : prev.unit_price) || 0;
      if (qty > 0 && price > 0) {
        updated.commitment_amount = parseFloat((qty * price).toFixed(2));
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!formData.project_id || !formData.wbs_element_id) {
      toast.error('Please select project and WBS element'); return;
    }
    if (!formData.source_document_number) {
      toast.error('Please enter source document number'); return;
    }
    if (formData.commitment_amount <= 0) {
      toast.error('Please enter a valid amount'); return;
    }

    try {
      const payload = { ...formData };
      // Convert empty strings to null for optional fields
      ['expected_delivery_date', 'material_id', 'material_description', 'unit_of_measure', 'cost_element_id', 'source_document_item'].forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      ['quantity', 'unit_price'].forEach(key => {
        payload[key] = payload[key] === '' || payload[key] === null ? null : parseFloat(payload[key]) || null;
      });

      await psAPI.createCommitment(payload);
      toast.success('Commitment created successfully');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create commitment');
    }
  };

  const handleUpdate = async () => {
    if (formData.commitment_amount <= 0) {
      toast.error('Please enter a valid amount'); return;
    }
    try {
      const payload = { ...formData };
      ['expected_delivery_date', 'material_id', 'material_description', 'unit_of_measure', 'cost_element_id'].forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      ['quantity', 'unit_price'].forEach(key => {
        payload[key] = payload[key] === '' || payload[key] === null ? null : parseFloat(payload[key]) || null;
      });

      await psAPI.updateCommitment(selectedCommitment.commitment_id, payload);
      toast.success('Commitment updated successfully');
      setShowEditDialog(false);
      setSelectedCommitment(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commitment');
    }
  };

  const handleCancel = async () => {
    try {
      await psAPI.cancelCommitment(selectedCommitment.commitment_id);
      toast.success('Commitment cancelled');
      setShowCancelDialog(false);
      setSelectedCommitment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const openViewDialog = async (commit) => {
    try {
      const response = await psAPI.getCommitment(commit.commitment_id);
      setSelectedCommitment(response.data);
    } catch {
      setSelectedCommitment(commit);
    }
    setShowViewDialog(true);
  };

  const openEditDialog = (commit) => {
    setSelectedCommitment(commit);
    setFormData({
      project_id: commit.project_id,
      wbs_element_id: commit.wbs_element_id,
      commitment_type: commit.commitment_type,
      source_document_number: commit.source_document_number,
      source_document_item: commit.source_document_item || '',
      commitment_amount: commit.commitment_amount,
      currency: commit.currency || 'USD',
      fiscal_year: commit.fiscal_year,
      period: commit.period,
      cost_element_id: commit.cost_element_id || '',
      commitment_date: commit.commitment_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      expected_delivery_date: commit.expected_delivery_date?.split('T')[0] || '',
      material_id: commit.material_id || '',
      material_description: commit.material_description || '',
      quantity: commit.quantity ?? '',
      unit_of_measure: commit.unit_of_measure || '',
      unit_price: commit.unit_price ?? '',
      description: commit.description || ''
    });
    fetchWBSElements(commit.project_id);
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setWbsElements([]);
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const config = {
      'OPEN': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'PARTIALLY_CONSUMED': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      'FULLY_CONSUMED': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      'CANCELLED': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' }
    };
    const c = config[status] || config['OPEN'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const labels = { PR: 'Purchase Req.', PO: 'Purchase Order', FR: 'Funds Reservation', MANUAL: 'Manual' };
    return labels[type] || type;
  };

  const stats = {
    total: commitments.length,
    openAmount: commitments.filter(c => c.commitment_status === 'OPEN').reduce((sum, c) => sum + (c.remaining_amount || 0), 0),
    consumedAmount: commitments.reduce((sum, c) => sum + (c.consumed_amount || 0), 0),
    totalAmount: commitments.reduce((sum, c) => sum + (c.commitment_amount || 0), 0)
  };

  const filteredCommitments = commitments.filter(c =>
    (c.commitment_id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (c.source_document_number?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (c.material_id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (c.wbs_element_name?.toLowerCase() || c.wbs_element_id?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Shared material assignment section for create/edit
  const renderMaterialSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
        <Package className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">Material Assignment</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Material ID</Label>
          <Input
            placeholder="e.g., MAT-100001"
            value={formData.material_id}
            onChange={(e) => setFormData(prev => ({ ...prev, material_id: e.target.value }))}
            className="bg-slate-50"
            data-testid="form-material-id"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Material Description</Label>
          <Input
            placeholder="e.g., Steel pipes 6 inch"
            value={formData.material_description}
            onChange={(e) => setFormData(prev => ({ ...prev, material_description: e.target.value }))}
            className="bg-slate-50"
            data-testid="form-material-desc"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Quantity</Label>
          <Input
            type="number" min="0" step="0.01"
            placeholder="0"
            value={formData.quantity}
            onChange={(e) => updateMaterialCalc('quantity', e.target.value)}
            className="bg-slate-50"
            data-testid="form-quantity"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Unit of Measure</Label>
          <Select
            value={formData.unit_of_measure || '__none__'}
            onValueChange={(v) => setFormData(prev => ({ ...prev, unit_of_measure: v === '__none__' ? '' : v }))}
          >
            <SelectTrigger className="bg-slate-50" data-testid="form-uom">
              <Ruler className="h-3.5 w-3.5 mr-2 text-slate-400" />
              <SelectValue placeholder="Select unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select unit</SelectItem>
              {unitsOfMeasure.length > 0 ? (
                unitsOfMeasure.map(u => (
                  <SelectItem key={u.unit_code} value={u.unit_code}>{u.unit_code}{u.name ? ` - ${u.name}` : ''}</SelectItem>
                ))
              ) : (
                <>
                  <SelectItem value="EA">EA - Each</SelectItem>
                  <SelectItem value="PC">PC - Piece</SelectItem>
                  <SelectItem value="KG">KG - Kilogram</SelectItem>
                  <SelectItem value="L">L - Liter</SelectItem>
                  <SelectItem value="M">M - Meter</SelectItem>
                  <SelectItem value="SET">SET - Set</SelectItem>
                  <SelectItem value="BOX">BOX - Box</SelectItem>
                  <SelectItem value="HR">HR - Hour</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Unit Price</Label>
          <Input
            type="number" min="0" step="0.01"
            placeholder="0.00"
            value={formData.unit_price}
            onChange={(e) => updateMaterialCalc('unit_price', e.target.value)}
            className="bg-slate-50"
            data-testid="form-unit-price"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-orange-600">Calculated Amount</Label>
          <div className="h-10 flex items-center px-3 rounded-md border border-orange-200 bg-orange-50 text-sm font-semibold text-orange-700" data-testid="calculated-amount">
            {formData.quantity && formData.unit_price
              ? formatCurrency(parseFloat(formData.quantity) * parseFloat(formData.unit_price), formData.currency)
              : 'Enter quantity & unit price'}
          </div>
        </div>
      </div>
    </div>
  );

  // Shared form fields for create/edit
  const renderFormFields = (isEdit = false) => (
    <Tabs defaultValue="document" className="space-y-4">
      <TabsList className="bg-slate-100">
        <TabsTrigger value="document" data-testid="tab-document">Document</TabsTrigger>
        <TabsTrigger value="material" data-testid="tab-material">
          Material {formData.material_id && <span className="ml-1 text-xs text-orange-600">*</span>}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="document" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Project <span className="text-red-500">*</span></Label>
            {isEdit ? (
              <Input value={projects.find(p => p.project_id === formData.project_id)?.name || formData.project_id} disabled className="bg-slate-100" />
            ) : (
              <Select value={formData.project_id || '__none__'} onValueChange={(v) => handleProjectChange(v === '__none__' ? '' : v)}>
                <SelectTrigger className="bg-slate-50" data-testid="form-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select project</SelectItem>
                  {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">WBS Element <span className="text-red-500">*</span></Label>
            {isEdit ? (
              <Input value={wbsElements.find(w => w.wbs_element_id === formData.wbs_element_id)?.name || formData.wbs_element_id} disabled className="bg-slate-100" />
            ) : (
              <Select value={formData.wbs_element_id || '__none__'} onValueChange={(v) => setFormData(prev => ({ ...prev, wbs_element_id: v === '__none__' ? '' : v }))} disabled={!formData.project_id}>
                <SelectTrigger className="bg-slate-50" data-testid="form-wbs"><SelectValue placeholder="Select WBS element" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select WBS element</SelectItem>
                  {wbsElements.map(w => <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Commitment Type <span className="text-red-500">*</span></Label>
            <Select value={formData.commitment_type} onValueChange={(v) => setFormData(prev => ({ ...prev, commitment_type: v }))}>
              <SelectTrigger className="bg-slate-50" data-testid="form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PR">Purchase Requisition</SelectItem>
                <SelectItem value="PO">Purchase Order</SelectItem>
                <SelectItem value="FR">Funds Reservation</SelectItem>
                <SelectItem value="MANUAL">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Source Document # <span className="text-red-500">*</span></Label>
            <Input placeholder="e.g., PO-12345" value={formData.source_document_number} onChange={(e) => setFormData(prev => ({ ...prev, source_document_number: e.target.value }))} className="bg-slate-50" data-testid="form-source-doc" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Document Item</Label>
            <Input placeholder="e.g., 10" value={formData.source_document_item} onChange={(e) => setFormData(prev => ({ ...prev, source_document_item: e.target.value }))} className="bg-slate-50" data-testid="form-doc-item" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Amount <span className="text-red-500">*</span></Label>
            <Input type="number" placeholder="0.00" value={formData.commitment_amount || ''} onChange={(e) => setFormData(prev => ({ ...prev, commitment_amount: parseFloat(e.target.value) || 0 }))} className="bg-slate-50" data-testid="form-amount" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Currency</Label>
            <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}>
              <SelectTrigger className="bg-slate-50" data-testid="form-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['USD', 'EUR', 'GBP', 'INR', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'SGD'].map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Fiscal Year</Label>
            <Select value={String(formData.fiscal_year)} onValueChange={(v) => setFormData(prev => ({ ...prev, fiscal_year: parseInt(v) }))}>
              <SelectTrigger className="bg-slate-50" data-testid="form-fy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Period</Label>
            <Select value={String(formData.period)} onValueChange={(v) => setFormData(prev => ({ ...prev, period: parseInt(v) }))}>
              <SelectTrigger className="bg-slate-50" data-testid="form-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...Array(12)].map((_, i) => <SelectItem key={i + 1} value={String(i + 1)}>Period {i + 1}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Cost Element</Label>
            <Select value={formData.cost_element_id || '__none__'} onValueChange={(v) => setFormData(prev => ({ ...prev, cost_element_id: v === '__none__' ? '' : v }))}>
              <SelectTrigger className="bg-slate-50" data-testid="form-cost-element"><SelectValue placeholder="Select cost element" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {costElements.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500" data-testid="cost-elements-empty">
                    No cost elements found. Create them in Controlling → Cost Elements.
                  </div>
                )}
                {costElements.map(ce => <SelectItem key={ce.cost_element_id} value={ce.cost_element_id}>{ce.cost_element_id} - {ce.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Commitment Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={formData.commitment_date} onChange={(e) => setFormData(prev => ({ ...prev, commitment_date: e.target.value }))} className="bg-slate-50" data-testid="form-date" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Expected Delivery</Label>
            <Input type="date" value={formData.expected_delivery_date} onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))} className="bg-slate-50" data-testid="form-delivery" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Description</Label>
          <Textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-50" data-testid="form-description" />
        </div>
      </TabsContent>

      <TabsContent value="material">
        {renderMaterialSection()}
      </TabsContent>
    </Tabs>
  );

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="commitments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ps/planning')} className="hover:bg-slate-100" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-600 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Commitments</h1>
              <p className="text-sm text-slate-500">Track purchase orders, requisitions, and reservations</p>
            </div>
          </div>
        </div>
        {canWrite('project_system') && (
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-orange-600 hover:bg-orange-700" data-testid="new-commitment-btn">
          <Plus className="h-4 w-4 mr-2" />New Commitment
        </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Open Commitments</p>
                <p className="text-2xl font-bold text-orange-700" data-testid="stat-open">{formatCurrency(stats.openAmount)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl"><Clock className="h-5 w-5 text-orange-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Partially Consumed</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(commitments.filter(c => c.commitment_status === 'PARTIALLY_CONSUMED').reduce((sum, c) => sum + (c.remaining_amount || 0), 0))}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl"><AlertCircle className="h-5 w-5 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">Consumed Total</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.consumedAmount)}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl"><TrendingUp className="h-5 w-5 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Commitments</p>
                <p className="text-2xl font-bold text-slate-700" data-testid="stat-total">{stats.total}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl"><FileText className="h-5 w-5 text-slate-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search by ID, document, material, or WBS..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="search-input" />
            </div>
            <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({...filters, project_id: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200" data-testid="filter-project">
                <Building2 className="h-4 w-4 mr-2 text-slate-400" /><SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.commitment_status || "all"} onValueChange={(v) => setFilters({...filters, commitment_status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[170px] bg-slate-50 border-slate-200" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="PARTIALLY_CONSUMED">Partially Consumed</SelectItem>
                <SelectItem value="FULLY_CONSUMED">Fully Consumed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(filters.fiscal_year)} onValueChange={(v) => setFilters({...filters, fiscal_year: parseInt(v)})}>
              <SelectTrigger className="w-[120px] bg-slate-50 border-slate-200" data-testid="filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="font-semibold text-slate-600">ID</TableHead>
              <TableHead className="font-semibold text-slate-600">Type</TableHead>
              <TableHead className="font-semibold text-slate-600">Source Doc</TableHead>
              <TableHead className="font-semibold text-slate-600">Material</TableHead>
              <TableHead className="font-semibold text-slate-600">WBS Element</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Qty</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Amount</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Remaining</TableHead>
              <TableHead className="font-semibold text-slate-600">Status</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12">
                <div className="flex items-center justify-center gap-2 text-slate-500">
                  <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>Loading...
                </div>
              </TableCell></TableRow>
            ) : filteredCommitments.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12">
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-10 w-10 text-slate-300" />
                  <p className="text-slate-500">No commitments found</p>
                </div>
              </TableCell></TableRow>
            ) : filteredCommitments.map((commit) => (
              <TableRow key={commit.commitment_id} className="group hover:bg-orange-50/50 transition-colors" data-testid={`commitment-row-${commit.commitment_id}`}>
                <TableCell><span className="font-mono text-sm font-medium text-orange-600">{commit.commitment_id}</span></TableCell>
                <TableCell><Badge variant="outline" className="font-medium">{getTypeLabel(commit.commitment_type)}</Badge></TableCell>
                <TableCell className="text-slate-600">{commit.source_document_number}</TableCell>
                <TableCell>
                  {commit.material_id ? (
                    <div className="flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm text-slate-700">{commit.material_id}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-600">{commit.wbs_element_name || commit.wbs_element_id}</TableCell>
                <TableCell className="text-right text-sm text-slate-600">
                  {commit.quantity ? `${commit.quantity} ${commit.unit_of_measure || ''}` : '-'}
                </TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(commit.commitment_amount)}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(commit.remaining_amount)}</TableCell>
                <TableCell>{getStatusBadge(commit.commitment_status)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openViewDialog(commit)} title="View" data-testid={`view-btn-${commit.commitment_id}`}>
                      <Eye className="h-4 w-4 text-slate-600" />
                    </Button>
                    {commit.commitment_status === 'OPEN' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openEditDialog(commit)} title="Edit" data-testid={`edit-btn-${commit.commitment_id}`}>
                          <Edit2 className="h-4 w-4 text-slate-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-red-100" onClick={() => { setSelectedCommitment(commit); setShowCancelDialog(true); }} title="Cancel" data-testid={`cancel-btn-${commit.commitment_id}`}>
                          <XCircle className="h-4 w-4 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredCommitments.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-sm text-slate-500">Showing <span className="font-medium text-slate-700">{filteredCommitments.length}</span> commitments</p>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="create-dialog-title">Create Commitment</DialogTitle>
          </DialogHeader>
          {renderFormFields(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="create-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700" data-testid="create-submit-btn">Create Commitment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle data-testid="view-dialog-title">Commitment: {selectedCommitment?.commitment_id}</DialogTitle>
              {selectedCommitment && getStatusBadge(selectedCommitment.commitment_status)}
            </div>
          </DialogHeader>
          {selectedCommitment && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div><p className="text-xs text-slate-500">Project</p><p className="font-medium">{selectedCommitment.project_name || selectedCommitment.project_id}</p></div>
                <div><p className="text-xs text-slate-500">WBS Element</p><p className="font-medium">{selectedCommitment.wbs_element_name || selectedCommitment.wbs_element_id}</p></div>
                <div><p className="text-xs text-slate-500">Type</p><p className="font-medium">{getTypeLabel(selectedCommitment.commitment_type)}</p></div>
                <div><p className="text-xs text-slate-500">Source Document</p><p className="font-medium">{selectedCommitment.source_document_number}{selectedCommitment.source_document_item ? ` / ${selectedCommitment.source_document_item}` : ''}</p></div>
                <div><p className="text-xs text-slate-500">Fiscal Year</p><p className="font-medium">{selectedCommitment.fiscal_year}</p></div>
                <div><p className="text-xs text-slate-500">Period</p><p className="font-medium">{selectedCommitment.period}</p></div>
              </div>

              {/* Material Assignment Info */}
              {selectedCommitment.material_id && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4 text-orange-600" />
                    <p className="text-sm font-semibold text-orange-700">Material Assignment</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><p className="text-xs text-orange-500">Material ID</p><p className="font-medium text-orange-900" data-testid="view-material-id">{selectedCommitment.material_id}</p></div>
                    <div className="col-span-2"><p className="text-xs text-orange-500">Description</p><p className="font-medium text-orange-900" data-testid="view-material-desc">{selectedCommitment.material_description || '-'}</p></div>
                    <div><p className="text-xs text-orange-500">Quantity</p><p className="font-medium text-orange-900" data-testid="view-quantity">{selectedCommitment.quantity ?? '-'} {selectedCommitment.unit_of_measure || ''}</p></div>
                    <div><p className="text-xs text-orange-500">Unit Price</p><p className="font-medium text-orange-900" data-testid="view-unit-price">{selectedCommitment.unit_price ? formatCurrency(selectedCommitment.unit_price) : '-'}</p></div>
                    <div><p className="text-xs text-orange-500">Total Value</p><p className="font-semibold text-orange-900">{selectedCommitment.quantity && selectedCommitment.unit_price ? formatCurrency(selectedCommitment.quantity * selectedCommitment.unit_price) : '-'}</p></div>
                  </div>
                </div>
              )}

              {/* Amount Details */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-orange-50 rounded-lg">
                <div><p className="text-xs text-orange-600">Original Amount</p><p className="font-bold text-orange-700">{formatCurrency(selectedCommitment.commitment_amount)}</p></div>
                <div><p className="text-xs text-emerald-600">Consumed</p><p className="font-bold text-emerald-700">{formatCurrency(selectedCommitment.consumed_amount)}</p></div>
                <div><p className="text-xs text-slate-600">Remaining</p><p className="font-bold text-slate-700">{formatCurrency(selectedCommitment.remaining_amount)}</p></div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-100 rounded-lg">
                <div><p className="text-xs text-slate-500">Commitment Date</p><p className="font-medium">{selectedCommitment.commitment_date?.split('T')[0] || '-'}</p></div>
                <div><p className="text-xs text-slate-500">Expected Delivery</p><p className="font-medium">{selectedCommitment.expected_delivery_date?.split('T')[0] || '-'}</p></div>
              </div>

              {selectedCommitment.description && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Description</p>
                  <p className="text-sm text-slate-700">{selectedCommitment.description}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedCommitment?.commitment_status === 'OPEN' && (
              <Button onClick={() => { setShowViewDialog(false); openEditDialog(selectedCommitment); }} className="bg-orange-600 hover:bg-orange-700" data-testid="view-edit-btn">
                <Edit2 className="h-4 w-4 mr-2" /> Edit
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowViewDialog(false)} data-testid="view-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { resetForm(); setSelectedCommitment(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="edit-dialog-title">Edit Commitment: {selectedCommitment?.commitment_id}</DialogTitle>
          </DialogHeader>
          {renderFormFields(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={handleUpdate} className="bg-orange-600 hover:bg-orange-700" data-testid="edit-submit-btn">Update Commitment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Commitment</DialogTitle></DialogHeader>
          <p className="text-slate-600">Are you sure you want to cancel commitment <strong>{selectedCommitment?.commitment_id}</strong>?</p>
          <div className="p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600">Remaining amount: {formatCurrency(selectedCommitment?.remaining_amount)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} data-testid="cancel-dialog-no">No, Keep It</Button>
            <Button variant="destructive" onClick={handleCancel} data-testid="cancel-dialog-yes">Yes, Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default Commitments;
